import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Logo } from "../components/Logo";
import { IMG_AUTH_BG } from "../lib/images";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get("role") === "photographer" ? "photographer" : "stranger");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await register({ ...form, role });
      navigate(user.role === "stranger" ? "/register" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-2" data-testid="signup-page">
      <div className="hidden lg:block relative">
        <img src={IMG_AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-midnight-black/50" />
      </div>
      <div className="flex items-center justify-center px-6 py-16 bg-clean-white">
        <div className="w-full max-w-sm">
          <div className="bg-midnight-black rounded-xl px-4 py-3 inline-block mb-8">
            <Logo className="h-9" />
          </div>
          <h1 className="font-extrabold uppercase text-2xl tracking-tight mb-2">Create Your Account</h1>
          <p className="text-midnight-black/60 font-medium mb-6 text-sm">First, tell us which kind of account you want.</p>

          <div className="grid grid-cols-2 gap-3 mb-8">
            <button
              type="button"
              data-testid="signup-role-stranger"
              onClick={() => setRole("stranger")}
              className={`rounded-xl border-2 p-4 text-left transition-colors ${role === "stranger" ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
            >
              <Users className="w-5 h-5 text-stranger-orange mb-2" />
              <p className="font-extrabold text-xs uppercase">Stranger</p>
            </button>
            <button
              type="button"
              data-testid="signup-role-photographer"
              onClick={() => setRole("photographer")}
              className={`rounded-xl border-2 p-4 text-left transition-colors ${role === "photographer" ? "border-stranger-orange bg-warm-white" : "border-soft-gray"}`}
            >
              <Camera className="w-5 h-5 text-stranger-orange mb-2" />
              <p className="font-extrabold text-xs uppercase">Photographer</p>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="signup-first-name-input" placeholder="First name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input data-testid="signup-last-name-input" placeholder="Last name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <Input data-testid="signup-email-input" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="signup-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input data-testid="signup-password-input" type="password" placeholder="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {error && (
              <p className="text-sm font-bold text-red-600" data-testid="signup-error">
                {error}
              </p>
            )}
            <Button type="submit" data-testid="signup-submit-button" disabled={loading} className="w-full bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold tracking-wide rounded-full py-6 mt-2">
              {loading ? "Creating account..." : role === "stranger" ? "CREATE STRANGER ACCOUNT" : "CREATE PHOTOGRAPHER ACCOUNT"}
            </Button>
          </form>
          <p className="text-sm font-medium text-midnight-black/60 mt-6">
            Already have an account?{" "}
            <Link to="/login" data-testid="go-to-login-link" className="text-stranger-orange font-bold hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
